import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Send, TestTube, AlertTriangle } from "lucide-react";
import { Navigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AdminSurveyEmail = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [testEmail, setTestEmail] = useState("");
  const [excludeEmails, setExcludeEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [showBlastConfirm, setShowBlastConfirm] = useState(false);
  const [result, setResult] = useState<{ enqueued: number; total_users?: number; excluded?: number } | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });
      if (!cancelled) setIsAdmin(!error && data === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || (user && isAdmin === null)) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;


  const parseExcludeList = (): string[] => {
    return excludeEmails
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setSending(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-survey-email", {
        body: { test_email: testEmail },
      });
      if (error) throw error;
      setResult(data);
      toast({ title: "Test email queued!", description: `Sent to ${testEmail}. Check your inbox in ~30 seconds.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleBlastAll = async () => {
    setShowBlastConfirm(false);
    setSending(true);
    setResult(null);
    try {
      const excluded = parseExcludeList();
      const { data, error } = await supabase.functions.invoke("send-survey-email", {
        body: { exclude_emails: excluded },
      });
      if (error) throw error;
      setResult(data);
      toast({ title: "Survey emails queued!", description: `${data.enqueued} emails queued for delivery.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const excludeCount = parseExcludeList().length;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Survey Email Sender</h1>
          <p className="text-sm text-muted-foreground">Send the pricing survey email to your users.</p>
        </div>

        {/* Test send */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Send test email</label>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <Button onClick={handleTestSend} disabled={sending || !testEmail} className="gap-2 shrink-0">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube className="h-4 w-4" />}
              Test
            </Button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Exclude emails */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">
            Exclude emails {excludeCount > 0 && <span className="text-muted-foreground">({excludeCount})</span>}
          </label>
          <Textarea
            placeholder={"Paste emails to exclude, one per line or comma-separated\ne.g. user1@example.com, user2@example.com"}
            value={excludeEmails}
            onChange={(e) => setExcludeEmails(e.target.value)}
            rows={3}
            className="text-sm"
          />
        </div>

        {/* Blast all */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Send to all users</label>
          <Button
            variant="destructive"
            onClick={() => setShowBlastConfirm(true)}
            disabled={sending}
            className="w-full gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send to All Users {excludeCount > 0 && `(excluding ${excludeCount})`}
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div className="rounded-xl bg-muted p-4 text-sm text-foreground">
            ✅ <strong>{result.enqueued}</strong> email(s) queued for delivery.
            {result.excluded !== undefined && result.excluded > 0 && (
              <span className="text-muted-foreground"> ({result.excluded} excluded)</span>
            )}
            {result.total_users !== undefined && <span className="text-muted-foreground"> ({result.total_users} total users)</span>}
          </div>
        )}
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={showBlastConfirm} onOpenChange={setShowBlastConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Send to all users?
            </DialogTitle>
            <DialogDescription>
              This will send the survey email to every registered user
              {excludeCount > 0 && ` except ${excludeCount} excluded email(s)`}.
              This action cannot be undone. Make sure you've tested with a test email first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlastConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBlastAll}>Yes, send to everyone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSurveyEmail;
